import { Data, UI, Utils } from 'common';
import { WrappedInputTextWithLabel } from 'common/src/code/ui/input';
import React, { useEffect, useState } from 'react';

const {
	WrappedDropdownWithLabel,
	WrappedButton,
	WrappedButtonType,
	Typography,
} = UI;
const { Analytics, Communication, Session } = Utils;
const { SegmentEventTypes, Capability } = Data;

interface PrintSelectorProps {
	className?: string;
}

const STORAGE_KEY = 'mailPrintSelectorSelections';

interface PrintSelections {
	selectedPostageType: string;
	paperType: string;
	size: string;
	serialNumber: string;
}

interface PaperTypeOption {
	label: string;
	value: string;
	icon: string;
}

interface PostageTile {
	id: string;
	title: string;
	description: string;
	icon: string;
	capability?: (typeof Capability)[keyof typeof Capability];
}

const PrintSelector: React.FC<PrintSelectorProps> = () => {
	const [showInput, setShowInput] = useState(false);
	const [labelValue, setLabelValue] = useState('');
	const [serialNumber, setSerialNumber] = useState('');
	const [serialNumberError, setSerialNumberError] = useState<string | false>(
		false
	);
	const [selectedPostageType, setSelectedPostageType] = useState<string>('');
	const [paperType, setPaperType] = useState<string>('');
	const [size, setSize] = useState<string>('');
	const [startPrintingDisabled, setStartPrintingDisabled] = useState(false);

	const [paperTypeOptions, setPaperTypeOptions] = useState<PaperTypeOption[]>([
		{
			label: 'Letter-Sized Paper',
			value: 'letter-sized',
			icon: 'letter-paper',
		},
		{ label: 'Label Sheet', value: 'label-sheet', icon: 'label-sheet' },
		{ label: 'Label Roll', value: 'label-roll', icon: 'label-roll' },
	]);

	const [sizeOptions, setSizeOptions] = useState([
		{ label: '8 ½" x 11"', value: '8.5x11' },
	]);

	const allPostageTiles: PostageTile[] = [
		{
			id: 'stamp',
			title: 'Stamp',
			description: 'Print on NetStamps® sheets or rolls and on envelopes.',
			icon: 'netstamp',
		},
		{
			id: 'shipping-label',
			title: 'Shipping Label',
			description:
				'Print on plain letter-sized paper and label sheets or rolls.',
			icon: 'shipping-label',
		},
		{
			id: 'certified-mail',
			title: 'Certified Mail®',
			description:
				'Print on Certified Mail label sheets or on Certified Mail envelopes.',
			icon: 'certified-mail',
			capability: Capability.CertifiedMail,
		},
	];

	// Filter postage tiles based on user capabilities
	const postageTiles = allPostageTiles.filter(
		tile => !tile.capability || Session.hasCapability(tile.capability as any)
	);

	const certifiedMailDefaultSizeOptions = [
		{
			label: 'SDC-3610',
			value: 'SDC-3610',
		},
		{
			label: 'SDC-3710',
			value: 'SDC-3710',
		},
		{
			label: 'SDC-3910',
			value: 'SDC-3910',
		},
		{
			label: 'SDC-3930',
			value: 'SDC-3930',
		},
	];

	const saveSelections = () => {
		const selections: PrintSelections = {
			selectedPostageType,
			paperType,
			size,
			serialNumber,
		};
		localStorage.setItem(STORAGE_KEY, JSON.stringify(selections));
	};

	const loadSelections = (): PrintSelections | null => {
		const saved = localStorage.getItem(STORAGE_KEY);
		if (saved) {
			return JSON.parse(saved) as PrintSelections;
		}
		return null;
	};

	useEffect(() => {
		const saved = loadSelections();
		if (saved) {
			// Restore postage type first, which sets up paper type options
			onPostageTileChange(saved.selectedPostageType);

			// Use setTimeout to ensure state updates from onPostageTileChange have processed
			setTimeout(() => {
				// Restore paper type, which sets up size options
				onPaperTypeChange(saved.paperType);

				// Use another setTimeout to ensure paper type state updates have processed
				setTimeout(() => {
					// Restore size and serial number
					setSize(saved.size);
					setSerialNumber(saved.serialNumber);
				}, 0);
			}, 0);
		} else {
			// Set default values
			setLabelValue('Size');
			setPaperType('letter-sized');
			setSize('8.5x11');
			setSerialNumber('');
			setSerialNumberError(false);
			setShowInput(false);
			setSelectedPostageType('shipping-label');
			setPaperTypeOptions([
				{
					label: 'Letter-Sized Paper',
					value: 'letter-sized',
					icon: 'letter-paper',
				},
				{ label: 'Label Sheet', value: 'label-sheet', icon: 'label-sheet' },
				{ label: 'Label Roll', value: 'label-roll', icon: 'label-roll' },
			]);
			setSizeOptions([{ label: '8 ½" x 11"', value: '8.5x11' }]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getPrintSelectorValue = (): string => {
		let printMedia = '';

		if (
			selectedPostageType === 'shipping-label' &&
			paperType === 'letter-sized' &&
			size === '8.5x11'
		) {
			printMedia = 'shippingLabel812x11';
		} else if (
			selectedPostageType === 'shipping-label' &&
			paperType === 'label-sheet' &&
			size === 'SDC-1200'
		) {
			printMedia = 'shippingLabel1200';
		} else if (
			selectedPostageType === 'shipping-label' &&
			paperType === 'label-sheet' &&
			size === '5.5x8.5'
		) {
			printMedia = 'shippingLabel512x812';
		} else if (
			selectedPostageType === 'shipping-label' &&
			paperType === 'label-roll' &&
			size === '4x6'
		) {
			printMedia = 'roll4x6';
		} else if (
			selectedPostageType === 'shipping-label' &&
			paperType === 'label-roll' &&
			size === '4.125x6.25'
		) {
			printMedia = 'roll418x614';
		} else if (
			selectedPostageType === 'certified-mail' &&
			paperType === 'certified-mail-label' &&
			size === 'SDC-3610'
		) {
			printMedia = 'mailingLabelCM3610';
		} else if (
			selectedPostageType === 'certified-mail' &&
			paperType === 'certified-mail-label' &&
			size === 'SDC-3710'
		) {
			printMedia = 'mailingLabelCM3710';
		} else if (
			selectedPostageType === 'certified-mail' &&
			paperType === 'certified-mail-label' &&
			size === 'SDC-3910'
		) {
			printMedia = 'mailingLabelCM3910';
		} else if (
			selectedPostageType === 'certified-mail' &&
			paperType === 'certified-mail-label' &&
			size === 'SDC-3930'
		) {
			printMedia = 'mailingLabelCM3930';
		} else if (
			selectedPostageType === 'certified-mail' &&
			paperType === 'certified-mail-envelope' &&
			size === 'SDC-3510'
		) {
			printMedia = 'envelopeCM3510';
		} else if (
			selectedPostageType === 'certified-mail' &&
			paperType === 'certified-mail-envelope' &&
			size === 'SDC-3520'
		) {
			printMedia = 'envelopeCM3520';
		} else if (
			selectedPostageType === 'certified-mail' &&
			paperType === 'certified-mail-envelope' &&
			size === 'SDC-3530'
		) {
			printMedia = 'envelopeCM3530';
		} else if (
			selectedPostageType === 'certified-mail' &&
			paperType === 'certified-mail-envelope' &&
			size === 'SDC-3810'
		) {
			printMedia = 'envelopeCM3810';
		} else if (
			selectedPostageType === 'certified-mail' &&
			paperType === 'certified-mail-envelope' &&
			size === 'SDC-3830'
		) {
			printMedia = 'envelopeCM3830';
		} else if (
			selectedPostageType === 'certified-mail' &&
			paperType === 'certified-mail-envelope' &&
			size === 'SDC-3820'
		) {
			printMedia = 'envelopeCM3820';
		} else if (
			selectedPostageType === 'stamp' &&
			paperType === 'envelope' &&
			size === 'envelope10'
		) {
			printMedia = 'envelope10';
		} else if (
			selectedPostageType === 'stamp' &&
			paperType === 'envelope' &&
			size === 'envelope9'
		) {
			printMedia = 'envelope9';
		} else if (
			selectedPostageType === 'stamp' &&
			paperType === 'envelope' &&
			size === 'envelopeA9'
		) {
			printMedia = 'envelopeA9';
		} else if (
			selectedPostageType === 'stamp' &&
			paperType === 'envelope' &&
			size === 'envelope6'
		) {
			printMedia = 'envelope6';
		} else if (
			selectedPostageType === 'stamp' &&
			paperType === 'envelope' &&
			size === 'envelopeA2'
		) {
			printMedia = 'envelopeA2';
		} else if (
			selectedPostageType === 'stamp' &&
			paperType === 'envelope' &&
			size === 'envelope7'
		) {
			printMedia = 'envelope7';
		} else if (
			selectedPostageType === 'stamp' &&
			paperType === 'envelope' &&
			size === 'envelope11'
		) {
			printMedia = 'envelope11';
		} else if (
			selectedPostageType === 'stamp' &&
			paperType === 'envelope' &&
			size === 'envelope12'
		) {
			printMedia = 'envelope12';
		}

		if (
			selectedPostageType === 'stamp' &&
			serialNumber &&
			serialNumber.trim()
		) {
			return `netStamps-${serialNumber.trim()}`;
		}

		return printMedia;
	};

	const startPrinting = async () => {
		setStartPrintingDisabled(true);
		Analytics.trackSegmentEvent({
			trackingData: {
				postageType: selectedPostageType,
				paperType: paperType,
				size: size,
				serialNumber: serialNumber,
			},
			eventType: SegmentEventTypes.UserInteraction,
		});

		// Save current selections to localStorage (for same-domain persistence)
		saveSelections();

		const response = await Communication.getUrl({
			app: 'mail',
			route: undefined,
			applicationContext: undefined,
			urlType: 'WebClientApp',
		});

		if (Communication.isSuccessful(response)) {
			const baseUrl = response.data;
			const printSelectorValue = getPrintSelectorValue();
			const params = new URLSearchParams({
				printSelector: printSelectorValue,
			});

			if (window.location.href.includes('?')) {
				window.location.href = `${baseUrl}&${params.toString()}`;
			} else {
				window.location.href = `${baseUrl}?${params.toString()}`;
			}
		} else {
			setStartPrintingDisabled(false);
			console.error('Failed to get mail URL');
		}
	};

	const onPostageTileChange = (id: string) => {
		setSelectedPostageType(id);
		if (id === 'stamp') {
			setPaperTypeOptions([
				{
					label: 'NetStamps® Sheet or Roll',
					value: 'sheet',
					icon: 'netstamp',
				},
				{
					label: 'Envelope',
					value: 'envelope',
					icon: 'envelope-icon',
				},
			]);
			setPaperType('sheet');
			setSizeOptions([]);
			setShowInput(true);
			setLabelValue('Size');
		} else if (id === 'shipping-label') {
			setPaperTypeOptions([
				{
					label: 'Letter-Sized Paper',
					value: 'letter-sized',
					icon: 'letter-paper',
				},
				{
					label: 'Label Sheet',
					value: 'label-sheet',
					icon: 'label-sheet',
				},
				{
					label: 'Label Roll',
					value: 'label-roll',
					icon: 'label-roll',
				},
			]);
			setPaperType('letter-sized');
			setSizeOptions([
				{
					label: '8 ½" x 11"',
					value: '8.5x11',
				},
			]);
			setSize('8.5x11');
			setShowInput(false);
			setLabelValue('Size');
		} else if (id === 'certified-mail') {
			setPaperTypeOptions([
				{
					label: 'Label',
					value: 'certified-mail-label',
					icon: 'certified-mail-label',
				},
				{
					label: 'Envelope',
					value: 'certified-mail-envelope',
					icon: 'envelope-icon',
				},
			]);
			setPaperType('certified-mail-label');
			setSizeOptions(certifiedMailDefaultSizeOptions);
			setSize('SDC-3610');
			setShowInput(false);
			setLabelValue('Template Number');
		}
	};

	const onPaperTypeChange = (id: string) => {
		setPaperType(id);
		if (id === 'sheet') {
			setSizeOptions([]);
			setShowInput(true);
			setLabelValue('Size');
			setSerialNumber('');
			setSerialNumberError(false);
		} else if (id === 'envelope') {
			setSizeOptions([
				{
					label: '#10, 4 ⅛" x 9 ½"',
					value: 'envelope10',
				},
				{
					label: '#9, 3 ⅞" x 8 ⅞"',
					value: 'envelope9',
				},
				{
					label: '#A9, 5 ¾" x 8 ¾"',
					value: 'envelopeA9',
				},
				{
					label: '#6, 3 ⅝" x 6 ½"',
					value: 'envelope6',
				},
				{
					label: '#A2, 4 ⅝" x 5 ¾"',
					value: 'envelopeA2',
				},
				{
					label: '#7, 3 ⅞" x 7 ½"',
					value: 'envelope7',
				},
				{
					label: '#11, 4 ½" x 10 ⅝"',
					value: 'envelope11',
				},
				{
					label: '#12, 4 ¾" x 11"',
					value: 'envelope12',
				},
			]);
			setShowInput(false);
			setSize('envelope10');
			setLabelValue('Size');
			setSerialNumber('');
			setSerialNumberError(false);
		} else if (id === 'label-sheet') {
			setSizeOptions([
				{
					label: 'SDC-1200, 4 ¼" x 6 ¾"',
					value: 'SDC-1200',
				},
				{
					label: '5 ½" x 8 ½"',
					value: '5.5x8.5',
				},
			]);
			setSize('SDC-1200');
		} else if (id === 'label-roll') {
			setSizeOptions([
				{
					label: '4" x 6"',
					value: '4x6',
				},
				{
					label: '4 ⅛" x 6 ¼"',
					value: '4.125x6.25',
				},
			]);
			setSize('4x6');
		} else if (id === 'certified-mail-label') {
			setSizeOptions(certifiedMailDefaultSizeOptions);
			setSize('SDC-3610');
		} else if (id === 'certified-mail-envelope') {
			setSizeOptions([
				{
					label: 'SDC-3510',
					value: 'SDC-3510',
				},
				{
					label: 'SDC-3520',
					value: 'SDC-3520',
				},
				{
					label: 'SDC-3530',
					value: 'SDC-3530',
				},
				{
					label: 'SDC-3810',
					value: 'SDC-3810',
				},
				{
					label: 'SDC-3820',
					value: 'SDC-3820',
				},
				{
					label: 'SDC-3830',
					value: 'SDC-3830',
				},
			]);
			setSize('SDC-3810');
			setShowInput(false);
		}
	};

	const validateSerialNumber = (value: string | null): string | false => {
		if (!value || value.trim() === '') {
			return false;
		}
		const serialNumberRegex =
			/^2\d{8}$|^A\d{5}$|^[CV]\d{5}$|^9\d{8}$|^K\d{5}$|^ML\d{4}$|^L\d{5}$|^MP\d{4}$|^P\d{5}$|^R\d{5}$|^B\d{5}$|^H\d{5}$|^S\d{5}$|D\d{5}D12345$|^WN\d{4}$|^DM\d{4}$/;
		return serialNumberRegex.test(value) ? false : 'Invalid serial number.';
	};

	const onSerialNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSerialNumber(value);
		// Validate on change to update button state
		setSerialNumberError(validateSerialNumber(value));
	};

	const onSerialNumberBlur = () => {
		setSerialNumberError(validateSerialNumber(serialNumber));
	};

	const isSerialNumberValid = () => {
		if (!showInput) return true; // Not required if input is not shown
		const trimmed = serialNumber.trim();
		if (trimmed === '') return false;
		return validateSerialNumber(trimmed) === false;
	};

	const isStartPrintingDisabled = () => {
		return showInput && !isSerialNumberValid();
	};

	const paperTypeItemTemplate = (option: PaperTypeOption | null) => {
		if (!option) return null;
		return (
			<div className="paper-type-option">
				<div className={`option-icon ${option.icon}`} />
				<span className="option-label">{option.label}</span>
			</div>
		);
	};

	const paperTypeValueTemplate = (option: PaperTypeOption | null) => {
		if (!option) return null;
		return (
			<div className="paper-type-value">
				<div className={`value-icon ${option.icon}`} />
				<span className="value-label">{option.label}</span>
			</div>
		);
	};

	return (
		<div className={`print-selector`}>
			<div className="content">
				<Typography.H2>What do you want to print today?</Typography.H2>
				<div className="postage-tiles">
					{postageTiles.map(tile => (
						<button
							key={tile.id}
							className={`tile ${
								selectedPostageType === tile.id ? 'selected' : ''
							}`}
							onClick={() => onPostageTileChange(tile.id)}
							type="button"
						>
							<div className={`icon ${tile.icon}`}></div>
							<div className="content">
								<Typography.H4>{tile.title}</Typography.H4>
								<Typography.P>{tile.description}</Typography.P>
							</div>
						</button>
					))}
				</div>

				<div className="options">
					<div className="dropdowns">
						<WrappedDropdownWithLabel
							label="Paper Type"
							uiElementConfig={{
								id: 'paper-type',
								dataqa: 'print-selector-paper-type',
								value: paperType,
								options: paperTypeOptions,
								onChange: e => onPaperTypeChange(e.value),
								optionLabel: 'label',
								optionValue: 'value',
								itemTemplate: paperTypeItemTemplate,
								valueTemplate: paperTypeValueTemplate,
							}}
						/>
						{showInput ? (
							<WrappedInputTextWithLabel
								label="Serial Number"
								isInputInvalid={serialNumberError !== false}
								errorMessage={serialNumberError}
								uiElementConfig={{
									id: 'serial-number',
									dataqa: 'serial-number',
									value: serialNumber,
									onChange: onSerialNumberChange,
									onBlur: onSerialNumberBlur,
								}}
							/>
						) : (
							<WrappedDropdownWithLabel
								label={labelValue}
								uiElementConfig={{
									id: 'size',
									dataqa: 'print-selector-size',
									value: size,
									options: sizeOptions,
									onChange: e => setSize(e.value),
									optionLabel: 'label',
									optionValue: 'value',
								}}
							/>
						)}
					</div>
					<div className="button-container">
						<WrappedButton
							label="Start Printing"
							onClick={startPrinting}
							buttonType={WrappedButtonType.Primary}
							disabled={isStartPrintingDisabled() || startPrintingDisabled}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PrintSelector;
